import { SalesforceItem } from '../../parser/ParserTypes';
import { DependenciesSideDialogContent } from './DependenciesSideDialogContent';
import { DocumentationCardContentRenderer } from './DocumentationCardContentRenderer';
import { ResizablePanelWrapper } from '../building-blocks/documentation-card/resizable-panel/ResizablePanelWrapper';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { AddToCanvasButton } from '../../pages/funnel-map-flow/dialogs/metadata-elements/AddToCanvasButton';
import { isMetadataElementInCanvas } from '../../pages/funnel-map-flow/dialogs/metadata-elements/helper';
import { SWEEP_ELEMENTS } from './helpers';
import { useDocumentationContext } from '../context/DocumentationContext';
import { UI_EVENTS } from '../../../services/events';
import { useDocumentationCard } from '../building-blocks/documentation-card/useDocumentationCard';
import { useTags } from './annotations/tags/useTags';
import { useSelector } from 'react-redux';
import { selectTagsList } from '../../../reducers/documentation/userInputsReducer';
import { getConfigurationItemColor } from '../helpers';
import { isCustomObjectByName } from '../cpq/utils';
import { useCallback } from 'react';

interface DocumentationCardProps {
  crmOrgId: string;
  configurationItem: SalesforceItem;
  type: string;
  staticView?: boolean;
  onCloseDocumentationDialog?: () => void;
}

export const DocumentationCard = ({
  crmOrgId,
  configurationItem,
  type,
  staticView,
  onCloseDocumentationDialog,
}: DocumentationCardProps) => {
  const isSweepElement = SWEEP_ELEMENTS.includes(type);
  const { isExpandedMode } = useExpandedMode();
  const { canvasFunnelMap } = useDocumentationContext();
  const { handleCloseCard } = useDocumentationCard();
  const { clearTagOrUserConfigurations } = useTags(crmOrgId);
  const tags = useSelector(selectTagsList);

  const isInCanvas = canvasFunnelMap
    ? isMetadataElementInCanvas({
        crmOrgId,
        metadataId: configurationItem?.metadataId ?? '',
        canvasFunnelMap,
      })
    : false;

  const objectName = configurationItem.objectApiNames?.[0];
  const isPaywalled = !!objectName && isCustomObjectByName(objectName);
  const color = getConfigurationItemColor(configurationItem, undefined, tags);

  const onCleanup = useCallback(() => {
    clearTagOrUserConfigurations();
  }, [clearTagOrUserConfigurations]);

  return (
    <ResizablePanelWrapper
      crmOrgId={crmOrgId}
      configurationItemId={configurationItem.id}
      staticView={staticView}
    >
      <DependenciesSideDialogContent
        crmOrgId={crmOrgId}
        item={configurationItem}
        type={type}
        onClose={handleCloseCard}
        color={color}
        isPaywalled={isPaywalled}
        paywallObjectName={objectName}
        onCleanup={onCleanup}
        headerContent={
          isExpandedMode ||
          !canvasFunnelMap ||
          isSweepElement ||
          !configurationItem.metadataId ? null : (
            <AddToCanvasButton
              isStaticView={true}
              isInCanvas={isInCanvas}
              element={configurationItem}
              callback={onCloseDocumentationDialog}
              crmOrgId={crmOrgId}
              biEventName={UI_EVENTS.documentationAddMetadataToCanvas}
            />
          )
        }
      >
        <DocumentationCardContentRenderer
          crmOrgId={crmOrgId}
          configurationItem={configurationItem}
          type={type}
          isReadOnly={false}
        />
      </DependenciesSideDialogContent>
    </ResizablePanelWrapper>
  );
};
